import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Autocomplete,
  Slider
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import { reviewMappingsApi, PendingMapping } from '../services/reviewMappingsApi'
import { nomenclatorV2Api, SegmentDefinitionResponse } from '../services/nomenclatorV2Api'
import toast from 'react-hot-toast'

const ReviewMappingsPage: React.FC = () => {
  const [mappings, setMappings] = useState<PendingMapping[]>([])
  const [nomenclator, setNomenclator] = useState<SegmentDefinitionResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(25)
  
  // Filters
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [studyIdFilter, setStudyIdFilter] = useState<string>('')
  
  // Dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedMapping, setSelectedMapping] = useState<PendingMapping | null>(null)
  const [selectedNomenclatorCode, setSelectedNomenclatorCode] = useState<string>('')
  const [confidenceValue, setConfidenceValue] = useState<number>(95)
  const [reviewNotes, setReviewNotes] = useState<string>('')
  const [actionLoading, setActionLoading] = useState(false)

  // Load mappings
  const loadMappings = async () => {
    try {
      setLoading(true)
      const filters: any = {
        page: page + 1,
        limit
      }
      
      if (sourceTypeFilter) filters.source_type = sourceTypeFilter
      if (studyIdFilter) filters.study_id = studyIdFilter
      if (statusFilter) filters.status = statusFilter
      
      const response = await reviewMappingsApi.getPendingMappings(filters)
      setMappings(response.items || [])
      setTotal(response.total || 0)
    } catch (error: any) {
      console.error('Error loading pending mappings:', error)
      toast.error(error.response?.data?.detail || 'Eroare la încărcarea mappings')
    } finally {
      setLoading(false)
    }
  }

  // Load nomenclator v2
  const loadNomenclator = async () => {
    try {
      const items = await nomenclatorV2Api.listSegmentDefinitions()
      setNomenclator(items)
    } catch (error: any) {
      console.error('Error loading nomenclator v2:', error)
      toast.error('Eroare la încărcarea nomenclatorului v2')
    }
  }

  useEffect(() => {
    loadMappings()
    loadNomenclator()
  }, [page, limit, statusFilter, sourceTypeFilter])

  // Handle approve
  const handleApprove = async () => {
    if (!selectedMapping || !selectedNomenclatorCode) {
      toast.error('Selectează un cod nomenclator')
      return
    }

    try {
      setActionLoading(true)
      await reviewMappingsApi.approveMapping(selectedMapping.id, {
        approved_code: selectedNomenclatorCode,
        confidence: confidenceValue / 100, // Convert 0-100 to 0.0-1.0
        review_notes: reviewNotes || undefined
      })
      toast.success('Mapping aprobat cu succes')
      setApproveDialogOpen(false)
      resetDialogs()
      loadMappings()
    } catch (error: any) {
      console.error('Error approving mapping:', error)
      toast.error(error.response?.data?.detail || 'Eroare la aprobare')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle reject
  const handleReject = async () => {
    if (!selectedMapping) return

    try {
      setActionLoading(true)
      await reviewMappingsApi.rejectMapping(selectedMapping.id, {
        review_notes: reviewNotes || undefined
      })
      toast.success('Mapping respins')
      setRejectDialogOpen(false)
      resetDialogs()
      loadMappings()
    } catch (error: any) {
      console.error('Error rejecting mapping:', error)
      toast.error(error.response?.data?.detail || 'Eroare la respingere')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle edit (doar pentru mappings pending)
  const handleEdit = async () => {
    if (!selectedMapping) return

    try {
      setActionLoading(true)
      await reviewMappingsApi.updateMapping(selectedMapping.id, {
        suggested_code: selectedNomenclatorCode || undefined,
        review_notes: reviewNotes || undefined
      })
      toast.success('Mapping actualizat')
      setEditDialogOpen(false)
      resetDialogs()
      loadMappings()
    } catch (error: any) {
      console.error('Error updating mapping:', error)
      toast.error(error.response?.data?.detail || 'Eroare la actualizare')
    } finally {
      setActionLoading(false)
    }
  }

  const resetDialogs = () => {
    setSelectedMapping(null)
    setSelectedNomenclatorCode('')
    setReviewNotes('')
    setConfidenceValue(95)
  }

  // Open approve dialog
  const openApproveDialog = (mapping: PendingMapping) => {
    setSelectedMapping(mapping)
    setSelectedNomenclatorCode(mapping.suggested_code || '')
    setReviewNotes('')
    setConfidenceValue(Math.round((mapping.confidence || 0) * 100))
    setApproveDialogOpen(true)
  }

  // Open reject dialog
  const openRejectDialog = (mapping: PendingMapping) => {
    setSelectedMapping(mapping)
    setReviewNotes('')
    setRejectDialogOpen(true)
  }

  // Open edit dialog (doar pentru mappings pending)
  const openEditDialog = (mapping: PendingMapping) => {
    setSelectedMapping(mapping)
    setSelectedNomenclatorCode(mapping.suggested_code || '')
    setReviewNotes(mapping.review_notes || '')
    setEditDialogOpen(true)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning'
      case 'approved':
        return 'success'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'În așteptare'
      case 'approved':
        return 'Aprobat'
      case 'rejected':
        return 'Respins'
      default:
        return status
    }
  }

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'dicom_tags':
        return 'DICOM'
      case 'easymedical':
        return 'EasyMedical'
      case 'manual':
        return 'Manual'
      default:
        return sourceType
    }
  }

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'dicom_tags':
        return 'primary'
      case 'easymedical':
        return 'secondary'
      case 'manual':
        return 'info'
      default:
        return 'default'
    }
  }

  // Filtrează nomenclator după modality dacă e disponibil și doar active
  const getFilteredNomenclator = () => {
    let filtered = nomenclator.filter(item => item.status === 'active')
    
    if (!selectedMapping) return filtered
    
    const context = selectedMapping.context_data || {}
    const modality = context.modality || context.parsed_modality
    
    if (modality) {
      filtered = filtered.filter(item => item.modality === modality)
    }
    
    return filtered
  }

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }
  const tableSx = {
    '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
    '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
    '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header - Compact */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
          Review Mapări
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadMappings} disabled={loading} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
          Actualizează
        </Button>
      </Box>

      {/* Filters - Compact inline */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
            <InputLabel>Tip Sursă</InputLabel>
            <Select value={sourceTypeFilter} label="Tip Sursă" onChange={(e) => { setSourceTypeFilter(e.target.value); setPage(0) }}>
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
              <MenuItem value="dicom_tags" sx={{ fontSize: '0.8rem' }}>DICOM</MenuItem>
              <MenuItem value="easymedical" sx={{ fontSize: '0.8rem' }}>EasyMedical</MenuItem>
              <MenuItem value="manual" sx={{ fontSize: '0.8rem' }}>Manual</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}>
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
              <MenuItem value="pending" sx={{ fontSize: '0.8rem' }}>În așteptare</MenuItem>
              <MenuItem value="approved" sx={{ fontSize: '0.8rem' }}>Aprobate</MenuItem>
              <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Respinse</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Study ID" value={studyIdFilter} onChange={(e) => setStudyIdFilter(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { setPage(0); loadMappings() } }} sx={{ flex: 1, minWidth: 150, ...inputSx }} />
          <Button variant="contained" onClick={() => { setPage(0); loadMappings() }} size="small" sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}>
            Filtrează
          </Button>
        </Box>
      </Paper>

      {/* Table - Compact */}
      <TableContainer component={Paper} sx={tableSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
              <TableCell>Sursă</TableCell>
              <TableCell>Text Sursă</TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Cod Sugerat</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Creat la</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            ) : mappings.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Nu există mappings</TableCell></TableRow>
            ) : (
              mappings.map((mapping) => (
                <TableRow key={mapping.id} hover>
                  <TableCell><Chip label={getSourceTypeLabel(mapping.source_type)} color={getSourceTypeColor(mapping.source_type) as any} size="small" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell>
                    <Tooltip title={mapping.source_text}>
                      <Typography sx={{ fontSize: '0.78rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mapping.source_text}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {mapping.source_type === 'dicom_tags' && mapping.context_data?.series_description && (
                      <Typography sx={{ fontSize: '0.72rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{mapping.context_data.series_description}</Typography>
                    )}
                    {mapping.source_type === 'easymedical' && (
                      <Box sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
                        {mapping.context_data?.parsed_modality && <Box>Mod: {mapping.context_data.parsed_modality}</Box>}
                        {mapping.context_data?.parsed_body_part && <Box>BP: {mapping.context_data.parsed_body_part}</Box>}
                      </Box>
                    )}
                    {!mapping.context_data && '-'}
                  </TableCell>
                  <TableCell>{mapping.suggested_code || '-'}</TableCell>
                  <TableCell><Chip label={`${Math.round(mapping.confidence * 100)}%`} size="small" color={mapping.confidence >= 0.8 ? 'success' : mapping.confidence >= 0.5 ? 'warning' : 'error'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell><Chip label={getStatusLabel(mapping.status)} color={getStatusColor(mapping.status) as any} size="small" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>{mapping.created_at ? new Date(mapping.created_at).toLocaleDateString('ro-RO') : '-'}</TableCell>
                  <TableCell>
                    {mapping.status === 'pending' ? (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Aprobă"><IconButton size="small" color="success" onClick={() => openApproveDialog(mapping)}><CheckCircleIcon /></IconButton></Tooltip>
                        <Tooltip title="Respinge"><IconButton size="small" color="error" onClick={() => openRejectDialog(mapping)}><CancelIcon /></IconButton></Tooltip>
                        <Tooltip title="Editează"><IconButton size="small" color="primary" onClick={() => openEditDialog(mapping)}><EditIcon /></IconButton></Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title="Vezi detalii"><IconButton size="small" onClick={() => openEditDialog(mapping)}><VisibilityIcon /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }, '& .MuiTablePagination-select': { fontSize: '0.75rem' } }}
        />
      </TableContainer>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aprobă Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Sursă: <strong>{selectedMapping?.source_text}</strong>
              </Typography>
              {selectedMapping?.suggested_code && (
                <Typography variant="body2">
                  Cod sugerat: <strong>{selectedMapping.suggested_code}</strong>
                </Typography>
              )}
            </Alert>
            
            <Autocomplete
              options={getFilteredNomenclator()}
              getOptionLabel={(option) => `${option.code} - ${option.display_name_ro || option.display_name}`}
              value={getFilteredNomenclator().find(item => item.code === selectedNomenclatorCode) || null}
              onChange={(_, newValue) => {
                setSelectedNomenclatorCode(newValue?.code || '')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cod Nomenclator *"
                  fullWidth
                  required
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.code}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.display_name_ro || option.display_name} ({option.modality})
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ mb: 2 }}
            />
            
            <Typography gutterBottom>
              Confidence: {confidenceValue}%
            </Typography>
            <Slider
              value={confidenceValue}
              onChange={(_, value) => setConfidenceValue(value as number)}
              min={0}
              max={100}
              step={1}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' }
              ]}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note (opțional)"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Anulează</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading || !selectedNomenclatorCode}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Aprobă'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Respinge Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Text sursă: <strong>{selectedMapping?.source_text}</strong>
              </Typography>
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note (opțional)"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Anulează</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Respinge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle>
          {selectedMapping?.status === 'pending' ? 'Editează Mapping' : 'Vezi Detalii Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { color: '#fff' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Text sursă: <strong>{selectedMapping?.source_text}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Status: <strong>{getStatusLabel(selectedMapping?.status || '')}</strong>
              </Typography>
            </Alert>
            {selectedMapping?.status === 'pending' ? (
              <>
                <Autocomplete
                  options={getFilteredNomenclator()}
                  getOptionLabel={(option) => `${option.code} - ${option.display_name_ro || option.display_name}`}
                  value={getFilteredNomenclator().find(item => item.code === selectedNomenclatorCode) || null}
                  onChange={(_, newValue) => {
                    setSelectedNomenclatorCode(newValue?.code || '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cod Nomenclator Sugerat"
                      fullWidth
                    />
                  )}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Note"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Adaugă note despre acest mapping..."
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Cod aprobat: <strong>{selectedMapping?.approved_code || '-'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Note: <strong>{selectedMapping?.review_notes || '-'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Review-uit de: <strong>{selectedMapping?.reviewed_by || '-'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Data review: <strong>
                    {selectedMapping?.reviewed_at
                      ? new Date(selectedMapping.reviewed_at).toLocaleString('ro-RO')
                      : '-'}
                  </strong>
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Închide</Button>
          {selectedMapping?.status === 'pending' && (
            <Button
              variant="contained"
              onClick={handleEdit}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Salvează'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReviewMappingsPage

